import React, { useState, useEffect } from 'react';
import {
  Box,
  Image,
  Text,
  Stack,
  Flex,
  useMediaQuery,
} from '@chakra-ui/react';
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';
import './StaticNotifications.css';
import { useUIStore } from '#store';
import { shallow } from 'zustand/shallow';
import { BrandHeading } from '../BrandHeading.jsx';

function formatDate(dateString) {
  const date = new Date(dateString);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  const hours = date.getHours();

  return `${month} ${day}, ${year}`;
}

function formatTime(dateString) {
  const date = new Date(dateString);
  const months = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  const hours = date.getHours();
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  const formattedHour = hours % 12 || 12;

  return `at ${formattedHour}:${minutes} ${ampm}`;
}

const formatSlashDate = (dateInput) => {
  const date = new Date(dateInput);
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const year = date.getFullYear();
  return `${month}/${day}/${year}`;
};

const StaticNotifications = () => {
  return (
    <Stack alignItems="center" overflowY="hidden">
      <BrandHeading
        mt={8}
        fontSize={{ base: '3xl', sm: '4xl', lg: '5xl' }}
        mb={0}
      >
        Our Recent Notifications
      </BrandHeading>
      <Text fontSize={['md', 'lg']}>
        Our users got the following notifications recently!
      </Text>
      <NotificaitonCarousel />
    </Stack>
  );
};

const NotificaitonCarousel = () => {
  const [isMobile] = useMediaQuery('(max-width: 464px)');
  const [currentMessage, setCurrentMessage] = useState(0);
  const { getNotifications, notifications } = useUIStore(
    (state) => ({
      getNotifications: state.getNotifications,
      notifications: state.notifications,
    }),
    shallow
  );
  useEffect(() => {
    setCurrentMessage(isMobile ? 0 : 2);
  }, [isMobile]);

  useEffect(() => {
    getNotifications();
  }, []);

  const messageWidth = 300;
  if (!notifications?.length) return null;

  return (
    <Flex
      position="relative"
      width="100%"
      mx="auto"
      pt={4}
      direction={'row'}
      justifyContent={'center'}
    >
      <Box position={'absolute'}>
        <Image src={'iphone.png'} maxW="sm" m="auto" />
      </Box>
      <Carousel
        additionalTransfrom={0}
        autoPlay
        arrows={false}
        autoPlaySpeed={4000}
        centerMode={false}
        className="notification-carousel"
        containerClass="container-with-dots"
        dotListClass=""
        focusOnSelect={false}
        infinite
        itemClass=""
        keyBoardControl
        minimumTouchDrag={80}
        pauseOnHover
        renderButtonGroupOutside={false}
        renderDotsOutside={false}
        responsive={{
          desktop: {
            breakpoint: {
              max: 3000,
              min: 1024,
            },
            items: 5,
            partialVisibilityGutter: 40,
          },
          mobile: {
            breakpoint: {
              max: 464,
              min: 0,
            },
            items: 1,
            partialVisibilityGutter: 30,
          },
          tablet: {
            breakpoint: {
              max: 1024,
              min: 464,
            },
            items: 3,
            partialVisibilityGutter: 30,
          },
        }}
        rewind={false}
        rewindWithAnimation={false}
        rtl={false}
        shouldResetAutoplay
        showDots={false}
        sliderClass=""
        slidesToSlide={1}
        swipeable={false}
        customTransition="transform 700ms ease-in-out"
        beforeChange={(nextSlide, { currentSlide, onMove }) => {
          setCurrentMessage((nextSlide - (isMobile ? 2 : 8)) % 10);
        }}
      >
        {notifications.map((notification, index) => (
          renderNotificationCard(index, currentMessage, notification, messageWidth)
        ))}
      </Carousel>
    </Flex>
  );
};

function renderNotificationCard(index, currentMessage, notification, messageWidth) {
  const notifyTimes = notification.notifications.map((notify) => new Date(notify.updatedAt));
  // extract the latest notification's time
  const latestNotifyTime = new Date(Math.max.apply(null, notifyTimes));
  let translateX = 0;
  let scale = 1;

  const totalItems = 10;
  const visibleRange = 2;

  if (index === currentMessage) {
    translateX = 0;
    scale = 1;
  } else {
    if (index === (currentMessage + 1) % totalItems 
    || index === (currentMessage + 2) % totalItems
    || index === (currentMessage + 3) % totalItems
  ) {
      translateX = 25;
    } else {
      translateX = -25;
    }
    scale = 0.8;
  }
  return (
    <Flex
      justifyContent={'center'}
      direction={'column'}
      alignItems={'center'}
      pt={4}
      filter={index === currentMessage ? 'none' : 'blur(2px)'}
      transform={`translateX(${translateX}px) scale(${scale})`}
      transition="transform 700ms ease-in-out"
    >
      <Text color="gray.500" my={2} fontSize={'sm'}>
        <Text fontWeight={700} display={'inline-block'} mr={1}>
          {formatDate(new Date(latestNotifyTime))}{' '}
        </Text>
        {formatTime(new Date(latestNotifyTime))}
      </Text>
      <Text
        w={`${messageWidth}px`}
        px={4}
        pt={4}
        key={index}
        color="white"
        fontSize="lg"
        fontWeight="semibold"
        bg={index === currentMessage ? 'brand.500' : 'gray.300'}
        borderRadius="2xl"
        boxShadow={'md'}
        h="180px"
        transition='background-color 500ms linear'
      >
        {`Spot at ${notification?.outingId?.name} for the dates of ${formatSlashDate(
          notification.startDate
        )} to ${formatSlashDate(notification.endDate)} for party of ${notification.groupSize} is available!`}
      </Text>
    </Flex>
  );
}


export default StaticNotifications;